import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@dia/i18n';
import { Button } from './Button';
import { cn } from './cn';

export type CapturedPhoto = {
  /** Client generated, and the same id the outbox uses, so a retry never duplicates. */
  id: string;
  file: File;
  previewUrl: string;
};

export type PhotoCaptureProps = {
  /**
   * Called once per photo, as soon as it is taken. The caller uploads it right
   * away: iOS has no Background Sync, so a batched upload would be lost.
   */
  onCapture: (photo: CapturedPhoto) => void;
  onRemove?: (id: string) => void;
  /** Optional transform, normally the resize-to-1600 pipeline from @dia/db. */
  prepare?: (file: File) => Promise<File>;
  label?: string;
  max?: number;
  required?: boolean;
  className?: string;
};

function newId(): string {
  return globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `photo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Photo first, words second. The rear camera opens straight from the button on
 * a phone, and the file picker is the fallback on a laptop.
 */
export function PhotoCapture({
  onCapture,
  onRemove,
  prepare,
  label,
  max = 4,
  required = false,
  className,
}: PhotoCaptureProps) {
  const { t } = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(
    () => () => {
      for (const p of photos) URL.revokeObjectURL(p.previewUrl);
    },
    [photos],
  );

  const handleFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList?.length) return;
      setBusy(true);
      try {
        for (const raw of Array.from(fileList).slice(0, max - photos.length)) {
          const file = prepare ? await prepare(raw) : raw;
          const photo: CapturedPhoto = { id: newId(), file, previewUrl: URL.createObjectURL(file) };
          setPhotos((prev) => [...prev, photo]);
          onCapture(photo);
        }
      } finally {
        setBusy(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [max, onCapture, photos.length, prepare],
  );

  const remove = (id: string) => {
    setPhotos((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
    onRemove?.(id);
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={max > 1}
        required={required && photos.length === 0}
        className="sr-only"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {photos.length > 0 && (
        <ul className="flex flex-wrap gap-2 list-none p-0 m-0">
          {photos.map((p) => (
            <li key={p.id} className="relative">
              <img
                src={p.previewUrl}
                alt=""
                className="h-24 w-24 object-cover rounded border border-line"
              />
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="absolute -top-2 -right-2 min-h-tap min-w-tap flex items-center justify-center text-caption bg-surface border border-line rounded-full px-2"
                aria-label={t('action.remove_photo')}
              >
                x
              </button>
            </li>
          ))}
        </ul>
      )}

      {photos.length < max && (
        <Button
          variant="secondary"
          block
          busy={busy}
          busyLabel={t('loading')}
          onClick={() => inputRef.current?.click()}
        >
          {label ?? t('action.take_photo')}
        </Button>
      )}
    </div>
  );
}
